import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Occurence } from 'src/app/model/breakdowns/breakdown';
import {
  ContratSav,
  InstallContract,
} from 'src/app/model/installation/installation.model';
import { InstallMes } from 'src/app/model/installations/installations.model';
import {
  Call,
  CallsCount,
  CallStat,
  CodePanne,
  ContractType,
} from '../../../model/call/call.model';
import {
  Personne,
  PersonneSocietes,
  SavSuivi,
} from '../../../model/suiviCall/suiviCall';
import {
  APIUrls,
  WebConfigurationService,
} from '../common/web-configuration.service';

@Injectable({
  providedIn: 'root',
})
export class CallService extends WebConfigurationService {
  constructor(protected http: HttpClient) {
    super(http);
  }

  /**
   * Get the 'Calls' datas
   * @returns an Observable
   */
  public getCallsListData(page: string): Observable<Call> {
    return this.authGetObs<Call>(this.getCallsListUrl(page));
  }

  /**
   * GET the 'Calls' details data
   * @returns an Observable
   */
  public getCallsDetailsData(id: string): Observable<SavSuivi> {
    return this.authGetObs<SavSuivi>(this.getCallsDetailsUrl(id));
  }

  /**
   * GET the 'Calls' details data
   * @returns an Observable
   */
  public getCallStateData(id: string): Observable<any> {
    return this.authGetObs<any>(this.getCallStateUrl(id));
  }

  /**
   * GET the name  data of the dap owner
   * @returns an Observable
   */
  public getNameOwnerData(id: number): Observable<Personne> {
    return this.authGetObs<Personne>(this.getNomPrenomProprietaireUrl(id));
  }

  /**
   * Get the 'raison sociale' and 'code EBD' data
   */
  public getRaisonSocialeData(id: string): Observable<PersonneSocietes> {
    return this.authGetObs<PersonneSocietes>(
      this.getRaisonSocialeUrl(id.toString())
    );
  }

  /**
   * Get the Installations data for a machine
   */
  public getContratSav(id: string): Observable<ContratSav> {
    return this.authGetObs<ContratSav>(this.getContratSavUrl(id));
  }

  /**
   * GET the total number of calls
   */
  public getCallsCount(): Observable<CallsCount> {
    return this.authGetObs<CallsCount>(APIUrls.getCallsCountUrl());
  }

  public getCallsCodes(codes: string[]) {
    return this.authGetObs<CallStat[]>(
      this.getCodesPannesStatsLastThirtyDays(codes)
    );
  }

  /**
   * Get a call list
   * @param contractType
   * @param startDate from
   * @param endDate to
   * @returns CallStat
   */
  public getCallsStats(
    contractType: number,
    startDate: Date,
    endDate: Date,
    dateMiseEnService: Date,
    action: string,
    intervenant: string,
    callsType: string,
    codePanne: string,
    techniciens: number,
    departements: any,
    validation: any
  ): Observable<CallStat[]> {
    let sDate = this.getFormattedDate(startDate);

    /* Add 1 day to end date to include it in sql request
     * /!\ Doing so, the next validation will automatically have a + to it's end date
     * endDate.setDate(endDate.getDate() + 1); <== don't !
     * Fix issue #42
     */
    let tmpDate = new Date(endDate);
    tmpDate.setDate(tmpDate.getDate() + 1);
    let eDate = this.getFormattedDate(endDate);
    let mesDate = this.getFormattedDate(dateMiseEnService);

    return this.authGetObs<CallStat[]>(
      this.getCallSattsUrl(
        contractType,
        sDate,
        eDate,
        mesDate,
        action,
        intervenant,
        callsType,
        codePanne,
        techniciens,
        departements,
        validation
      )
    );
  }

  public getCallsPromise(
    codePanne: string,
    startDate: Date,
    endDate: Date
  ): Promise<CallStat[]> {
    let sDate = this.getFormattedDate(startDate);
    let eDate = this.getFormattedDate(endDate);

    return this.authGetObs<CallStat[]>(
      this.getShortCallSattsUrl(codePanne, sDate, eDate)
    ).toPromise();
  }

  public getCodePannesPromise(
    codePanne: string,
    startDate: Date,
    endDate: Date
  ): Promise<Occurence[]> {
    let sDate = this.getFormattedDate(startDate);
    let eDate = this.getFormattedDate(endDate);

    return this.authGetObs<Occurence[]>(
      this.getCodePannesNbUrl(codePanne, sDate, eDate)
    ).toPromise();
  }

  /**
   * Get the list of contract types
   * @returns ContractType[]
   */
  public getContractsTypes(): Observable<ContractType[]> {
    return this.authGetObs<ContractType[]>(this.getContractsTypesUrl());
  }

  public getCodesPannes(): Observable<CodePanne[]> {
    return this.authGetObs<CodePanne[]>(this.getCodesPannesUrl());
  }

  /**
   * Get a call list
   * @param contractType
   * @param paramStart before / after / =
   * @param startDate from
   * @param paramEnd before / after / =
   * @param endDate to
   * @returns InstallContract
   */
  public getInstallListByContract(
    contractType: number,
    paramStart: string,
    startDate: Date,
    paramEnd: string,
    endDate1: Date,
    endDate2: Date
  ): Observable<InstallContract[]> {
    let sDate = this.getFormattedDate(startDate);
    let eDate1 = this.getFormattedDate(endDate1);
    let eDate2 = this.getFormattedDate(endDate2);

    return this.authGetObs<InstallContract[]>(
      this.getInstallContractsUrl(
        contractType,
        paramStart,
        sDate,
        paramEnd,
        eDate1,
        eDate2
      )
    );
  }

  /**
   * Get a call list
   * @param mesDate
   * @returns InstallsList
   */
  public getInstallServiceList(mesDate: Date): Observable<InstallMes[]> {
    let mDate = this.getFormattedDate(mesDate);
    return this.authGetObs<InstallMes[]>(this.getMesInstall(mDate));
  }

  /**
   * GET installations / machines with status 'Installees' Or 'Mise en Service' between two dates
   */
  public getInstallationsInstalleesAndMes(
    startDate: Date,
    endDate: Date
  ): Observable<any> {
    let sDate = this.getFormattedDate(startDate);
    endDate.setDate(endDate.getDate() + 1); // Add 1 day to end date to include it in sql request
    let eDate = this.getFormattedDate(endDate);
    return this.authGetObs<any>(this.getMesAndInstalleesListUrl(sDate, eDate));
  }

  /**
   * GET infos about check on calls
   */
  public getcheckInfos(): Observable<any> {
    return this.authGetObs<any>(this.getcheckInfosUrl());
  }

  /**
   * GET intervenants sav list (names)
   */
  public getIntervenantsSavList(): Observable<any> {
    return this.authGetObs<any>(this.getIntervenantsSavListUrl());
  }
  /**
   * GET intervenants sav list (names)
   */
  public getPeopleInstallList(id: number): Observable<any> {
    return this.authGetObs<any>(this.getPeopleInstallListUrl(id));
  }
  /**
   * GET all people
   */
  public getPeopleList(): Observable<any> {
    return this.authGetObs<any>(this.getPeopleListUrl());
  }

  /**
   * Post validation for
   * @param isOk
   */
  public postValidation(call: any) {
    return this.authPutObs<any>(this.postValidationUrl(), call);
  }

  /**  ***************** Call report ******************** */

  public postCall(formData: any) {
    return this.http.post(APIUrls.postCallUrl(), formData, {
      headers: this.createHeaders(),
    });
  }
}
